import { toFixed } from 'accounting-js';
import getCouponDiscountAmount from './getCouponDiscountAmount';
import checkHasValidStartDate from './checkHasValidStartDate';
import checkHasTrialPeriod from './checkHasTrialPeriod';

/**
 * @description Calculates the proportionate discount amounts
 * @param {number} recurringSubtotalAmount
 * @param {number} oneTimeSubtotalAmount
 * @param {number} setupFeeAmount
 * @param {object} coupon
 * @returns {{
 *   recurringDiscountAmount: number,
 *   oneTimeVariantsDiscountAmount: number,
 *   setupFeeDiscountAmount: number
 * }} An object containing the proportionate discount amounts
 * - recurringDiscountAmount: The discount amount applied to the recurring subtotal
 * - oneTimeVariantsDiscountAmount: The discount amount applied to one-time variants
 * - setupFeeDiscountAmount: The discount amount applied to the setup fee (if applicable)
 */
export function getProportionateDiscountAmounts({
  recurringSubtotalAmount = 0,
  oneTimeSubtotalAmount = 0,
  setupFeeAmount = 0,
  coupon,
  trialPeriodDays = 0,
  startDate = null,
}) {
  if (!coupon) {
    return {
      recurringDiscountAmount: 0,
      oneTimeVariantsDiscountAmount: 0,
      setupFeeDiscountAmount: 0,
      couponDiscountAmount: 0,
    };
  }

  const hasValidStartDate = checkHasValidStartDate(startDate);
  const hasTrialPeriod = checkHasTrialPeriod(trialPeriodDays);
  const hasSetupFee = setupFeeAmount > 0;
  const couponAppliesToSetupFee = coupon.appliesToSetupFee;

  // special case for coupons on subscriptions with setup fee
  // to prevent incorrect proportions
  if (
    recurringSubtotalAmount > 0 &&
    (hasValidStartDate || hasTrialPeriod) &&
    hasSetupFee &&
    couponAppliesToSetupFee
  ) {
    const couponDiscountAmount = getCouponDiscountAmount({
      amount: oneTimeSubtotalAmount,
      setupFeeAmount,
      coupon,
    });

    let totalAmount = oneTimeSubtotalAmount;
    totalAmount += setupFeeAmount;

    // calculate proportions
    let oneTimeProportion = 0;
    let setupFeeProportion = 0;
    if (totalAmount > 0) {
      oneTimeProportion = oneTimeSubtotalAmount / totalAmount;

      if (coupon && coupon.appliesToSetupFee) {
        setupFeeProportion = setupFeeAmount / totalAmount;
      }
    }

    const oneTimeVariantsDiscountAmount = couponDiscountAmount * oneTimeProportion;
    const setupFeeDiscountAmount =
      coupon && coupon.appliesToSetupFee ? couponDiscountAmount * setupFeeProportion : 0;

    const amountInCents = amount => Number(toFixed(amount, 0));

    if (coupon.duration === 'once') {
      return {
        recurringDiscountAmount: 0,
        oneTimeVariantsDiscountAmount: amountInCents(oneTimeVariantsDiscountAmount),
        setupFeeDiscountAmount: amountInCents(setupFeeDiscountAmount),
        couponDiscountAmount: amountInCents(couponDiscountAmount),
      };
    } else {
      return {
        recurringDiscountAmount: amountInCents(couponDiscountAmount),
        oneTimeVariantsDiscountAmount: amountInCents(oneTimeVariantsDiscountAmount),
        setupFeeDiscountAmount: amountInCents(setupFeeDiscountAmount),
        couponDiscountAmount: amountInCents(couponDiscountAmount),
      };
    }
  }

  const couponDiscountAmount = getCouponDiscountAmount({
    amount: recurringSubtotalAmount + oneTimeSubtotalAmount,
    setupFeeAmount,
    coupon,
  });

  let totalAmount = recurringSubtotalAmount + oneTimeSubtotalAmount;
  if (coupon && coupon.appliesToSetupFee) {
    totalAmount += setupFeeAmount;
  }

  // Calculate proportions
  let recurringProportion = 0;
  let oneTimeProportion = 0;
  let setupFeeProportion = 0;
  if (totalAmount > 0) {
    recurringProportion = recurringSubtotalAmount / totalAmount;
    oneTimeProportion = oneTimeSubtotalAmount / totalAmount;

    if (coupon && coupon.appliesToSetupFee) {
      setupFeeProportion = setupFeeAmount / totalAmount;
    }
  }

  // Split the discount proportionally
  const recurringDiscountAmount = couponDiscountAmount * recurringProportion;
  const oneTimeVariantsDiscountAmount = couponDiscountAmount * oneTimeProportion;
  const setupFeeDiscountAmount =
    coupon && coupon.appliesToSetupFee ? couponDiscountAmount * setupFeeProportion : 0;

  // Round to 2 decimal places
  const amountInCents = amount => Number(toFixed(amount, 0));

  return {
    recurringDiscountAmount: amountInCents(recurringDiscountAmount),
    oneTimeVariantsDiscountAmount: amountInCents(oneTimeVariantsDiscountAmount),
    setupFeeDiscountAmount: amountInCents(setupFeeDiscountAmount),
    couponDiscountAmount: amountInCents(couponDiscountAmount),
  };
}
